export const DEFAULT_NOTIFICATION_AVATAR =
  "https://cdn-icons-png.flaticon.com/512/847/847969.png";

export function normalizeNotificationTimestamp(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return null;
  return numericValue < 1e12 ? numericValue * 1000 : numericValue;
}

export function formatNotificationTimestamp(value) {
  const timestamp = normalizeNotificationTimestamp(value);
  if (timestamp == null) return "";

  const date = new Date(timestamp);
  const timeLabel = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const dateLabel = date.toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return `${dateLabel} • ${timeLabel}`;
}

export function getBellNotifications(notifications) {
  return notifications.filter(
    (notification) =>
      notification?.type !== "message" &&
      notification?.type !== "friend_request"
  );
}

export function sortNotificationsByCreatedAt(notifications) {
  return [...notifications].sort((left, right) => {
    const leftCreatedAt = Number(left?.createdAt || 0);
    const rightCreatedAt = Number(right?.createdAt || 0);
    return rightCreatedAt - leftCreatedAt;
  });
}

export function getLatestFriendRequestNotificationsByUserId(notifications) {
  return notifications.reduce((acc, notification) => {
    if (notification?.type !== "friend_request" || !notification?.fromUserId) {
      return acc;
    }

    const existing = acc[notification.fromUserId];
    const existingCreatedAt = Number(existing?.createdAt || 0);
    const nextCreatedAt = Number(notification?.createdAt || 0);

    if (!existing || nextCreatedAt > existingCreatedAt) {
      acc[notification.fromUserId] = notification;
    }

    return acc;
  }, {});
}

export function getNotificationAvatarSrc(user, apiBase) {
  return user?.avatar
    ? `${apiBase}${user.avatar}`
    : DEFAULT_NOTIFICATION_AVATAR;
}

export function getMobileNotificationText(notification, user, t) {
  const actorName =
    user?.username ||
    notification?.fromUsername ||
    notification?.requesterUsername ||
    t("someone", { defaultValue: "Someone" });

  switch (notification?.type) {
    case "trip_shared":
      return `${actorName} ${t("shared_trip_with_you")}`;
    case "group_invite":
      return `${actorName} ${t("groups_invited_you", {
        defaultValue: "invited you to join",
      })} ${notification?.groupName || ""}`.trim();
    case "group_join_request":
      return `${actorName} ${t("groups_requested_to_join", {
        defaultValue: "requested to join",
      })} ${notification?.groupName || ""}`.trim();
    case "group_post_pending":
      return `${actorName} ${t("groups_post_pending_notification", {
        defaultValue: "submitted a post in",
      })} ${notification?.groupName || ""} ${t(
        "groups_post_pending_notification_suffix",
        {
          defaultValue: "that needs approval.",
        }
      )}`.trim();
    case "group_join_request_approved":
      return t("notifications_group_join_request_approved");
    case "group_join_request_rejected":
      return t("notifications_group_join_request_rejected");
    case "group_post_approved":
      return t("notifications_group_post_approved");
    case "group_post_rejected":
      return t("notifications_group_post_rejected");
    case "location_shared":
      return `${actorName} ${t("shared_location_with_you")}`;
    case "follow":
      return `${actorName} ${t("followed_you")}`;
    case "friend_accepted":
      return `${actorName} ${t("friend_accepted_you")}`;
    case "like":
      return `${actorName} ${t("liked_your_post")}`;
    case "comment":
      return `${actorName} ${t("commented_on_post")}`;
    case "comment_like":
      return t("notifications_comment_like", { actorName });
    case "comment_reply":
      return t("notifications_comment_reply", { actorName });
    case "mention_post":
      return t("notifications_mention_post", { actorName });
    case "mention_comment":
      return t("notifications_mention_comment", { actorName });
    case "mention_reply":
      return t("notifications_mention_reply", { actorName });
    case "share":
      return `${actorName} ${t("shared_your_post", {
        defaultValue: "shared your post",
      })}`;
    default:
      return t("notifications_activity_generic", { actorName });
  }
}
